import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button, Input, message, Modal, Select, Space, Steps, Typography } from 'antd';
import {
  useEldKeys,
  useMigrateToLegal,
  useMigrateToNonLegal,
} from '../../../../../../../../Hooks/TT_ELD/LegalMigration';
import { apiErrorMessage, MigrationDirection } from '../../../../../../../../API/TT_ELD/legalMigration';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../../../Utils/eldTypeLabels';
import PricePicker from './PricePicker';
import { hasSubscription } from './SubscriptionSummary';

const { Text, Paragraph } = Typography;

const CONSEQUENCES: Record<MigrationDirection, string[]> = {
  to_legal: [
    `The company switches to ${STANDARD_ELD} immediately.`,
    'Drivers who are Off Duty / Sleeper Berth (and deactivated drivers) are migrated now and logged out.',
    'Drivers who are On Duty / Driving keep working as before and switch automatically the next time they go Off Duty or Sleeper Berth.',
    'The last 8 days of logs are imported in the background as requested edits that drivers accept in the app.',
  ],
  to_non_legal: [
    `The company switches back to ${DEFAULT_ELD} immediately.`,
    'Drivers who are Off Duty / Sleeper Berth (and deactivated drivers) are migrated now and logged out.',
    `Drivers who are On Duty / Driving keep working on ${STANDARD_ELD} and switch automatically the next time they go Off Duty or Sleeper Berth.`,
    'Their Legal logs from the last 8 days are imported in the background as requested logs.',
  ],
};

// The price can only be listed after the switch (allowed plans follow the company's ELD type), so a
// migrated company is remembered until its price is set, in case the modal is closed in between
const pendingKey = (companyId: string) => `eldMigration/pricePending/${companyId}`;
export const pricePending = {
  get(companyId: string) {
    try {
      return localStorage.getItem(pendingKey(companyId)) === '1';
    } catch {
      return false;
    }
  },
  set(companyId: string, pending: boolean) {
    try {
      if (pending) localStorage.setItem(pendingKey(companyId), '1');
      else localStorage.removeItem(pendingKey(companyId));
    } catch {
      // storage unavailable: the warning just won't survive a reload
    }
  },
};

type Step = 'migrate' | 'price';

interface Props {
  companyId: string;
  company: any;
  direction: MigrationDirection;
  // 'price' reopens on the price step for a company that is already migrated
  initialStep?: Step;
  onPricePendingChange(pending: boolean): void;
  onClose(): void;
}

// Two steps in one modal: start the migration, then pick the new subscription price. It closes only
// from the X icon (not the mask or Esc), so a stray click can't abandon the flow halfway.
const MigrationModal = ({
  companyId,
  company,
  direction,
  initialStep = 'migrate',
  onPricePendingChange,
  onClose,
}: Props) => {
  const queryClient = useQueryClient();
  const { data: eldKeys, isLoading: keysLoading } = useEldKeys();
  const migrateToLegal = useMigrateToLegal(companyId);
  const migrateToNonLegal = useMigrateToNonLegal(companyId);

  const [step, setStep] = useState<Step>(initialStep);
  const [eldKeyId, setEldKeyId] = useState<number | null>(null);
  const [typed, setTyped] = useState('');

  const toStandard = direction === 'to_legal';
  const companyName: string = company?.name || `Company #${companyId}`;
  const matches = typed.trim().toLowerCase() === companyName.trim().toLowerCase();
  const subscribed = hasSubscription(company);
  const starting = migrateToLegal.isLoading || migrateToNonLegal.isLoading;

  const defaultKey = eldKeys?.find((key) => key.is_default);
  const keyOptions = [
    { value: null as number | null, label: `Default key${defaultKey ? ` (${defaultKey.name})` : ''}` },
    ...(eldKeys ?? [])
      .filter((key) => !key.is_default)
      .map((key) => ({ value: key.id as number | null, label: `${key.name} · ${key.eld_provider}` })),
  ];

  const start = async () => {
    try {
      if (toStandard) await migrateToLegal.mutateAsync(eldKeyId);
      else await migrateToNonLegal.mutateAsync();
      // Allowed products/prices depend on the company type, which just changed: drop the cached
      // lists so the price step never shows the old type's plans
      queryClient.removeQueries(`getProducts/${companyId}`);
      queryClient.removeQueries(`getPrice/${companyId}`);
      if (!subscribed) {
        message.success('Migration started');
        onClose();
        return;
      }
      pricePending.set(companyId, true);
      onPricePendingChange(true);
      setStep('price');
    } catch (error) {
      message.error(apiErrorMessage(error));
    }
  };

  const priceSet = () => {
    pricePending.set(companyId, false);
    onPricePendingChange(false);
    onClose();
  };

  const requestClose = () => {
    if (step !== 'price') return onClose();
    Modal.confirm({
      title: 'Close without setting the price?',
      content: 'The company is already migrated, but its subscription stays on the old price. You can set it later from the "Price not set" warning on this tab.',
      okText: 'Close anyway',
      okButtonProps: { danger: true },
      cancelText: 'Back to price',
      onOk: onClose,
    });
  };

  const title =
    step === 'price'
      ? 'Set the subscription price'
      : toStandard
        ? `Switch this company to ${STANDARD_ELD}?`
        : `Switch this company back to ${DEFAULT_ELD}?`;

  return (
    <Modal
      open
      title={title}
      maskClosable={false}
      keyboard={false}
      onCancel={requestClose}
      footer={null}
      width={640}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {subscribed && (
          <Steps
            size="small"
            current={step === 'migrate' ? 0 : 1}
            items={[{ title: 'Migration' }, { title: 'Subscription price' }]}
          />
        )}

        {step === 'migrate' ? (
          <>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {CONSEQUENCES[direction].map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {subscribed ? (
              <Text type="secondary">
                Next step: choose the {toStandard ? STANDARD_ELD : DEFAULT_ELD} subscription price.
              </Text>
            ) : (
              <Text type="secondary">This company has no subscription, so there is no price to change.</Text>
            )}
            {toStandard && (
              <div>
                <Text>ELD key</Text>
                <Select
                  style={{ width: '100%' }}
                  loading={keysLoading}
                  value={eldKeyId}
                  onChange={setEldKeyId}
                  options={keyOptions}
                />
              </div>
            )}
            <div>
              <Paragraph style={{ marginBottom: 4 }}>
                Type the company name <Text strong>{companyName}</Text> to confirm:
              </Paragraph>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={companyName} autoFocus />
            </div>
            <div style={{ textAlign: 'right' }}>
              <Button type="primary" danger disabled={!matches} loading={starting} onClick={start}>
                Start migration
              </Button>
            </div>
          </>
        ) : (
          <PricePicker
            companyId={companyId}
            company={company}
            toStandard={toStandard}
            justMigrated={initialStep === 'migrate'}
            onDone={priceSet}
          />
        )}
      </Space>
    </Modal>
  );
};

export default MigrationModal;
